import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { map, RCCBadge, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi,RCCSwitch  } from "react-common-components";
import { useForm } from "react-hook-form";
import Title from "src/components/Title";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Audit from "src/components/Audit";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IRefIDimensionsModals, IDimensions, IComponentIDimensionsModals } from "./interface";
import addAndChangeSchema  from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefIDimensionsModals, IComponentIDimensionsModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { confirm } = useConfirmModal();
	const [selectedDimension, setSelectedDimension] = useState<IDimensions>({} as IDimensions);

	const [itens,setItens] = useState<Array<any>>([]);
    
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange } = useForm();
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			await addAndChangeSchema(translate).validate(data,{
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Dimensions/Insert",
					body: {
						...data,	
						taa011Ativo: data.taa011Ativo ? "S" : "N"
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Dimensions/Update",
					body: {
						...data,
						taa011Id: selectedDimension.taa011Id,
						taa011Ativo: data.taa011Ativo ? "S" : "N"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Dimensão adicionada com sucesso") : translate("Dimensão alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar dimensão") : translate("Erro ao alterar dimensão"), addToast);
		}
	};
	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedDimension({} as IDimensions); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: IDimensions) => {
		resetAddOrChange(); 
		setSelectedDimension(row); 
		setIsAdd(false); 
		modalAddOrChange.current?.openModal(); 
	};

	// Modal Reorder
	const modalReorderPosition = useRef<RCCRefModalProps>(null);
	const actionButtonReorder = () => {
		queryListData();
		modalReorderPosition.current?.openModal();	
	};
	const submitReorder =  async () => {	
		try{
			modalReorderPosition.current?.setLoadingButton();	

			const ListDTO = map({
				treeData:[...itens],
				getNodeKey:({treeIndex}:any) => treeIndex,
				ignoreCollapsed:false,
				callback:(info: any) => {
					const newNode = {...info.node};
					newNode.taa011Ordem = info.treeIndex+1;
					return newNode;
				}
			});
		
			await RCCServicesApi.postRequest({
				method: "Dimensions/Reorder",
				body: {
					ListDTO
				}
			});	

			addToast({
				color: "green",
				description: translate("Dimensões reordenadas com sucesso"),
				title: translate("Sucesso")
			});
			
			modalReorderPosition.current?.resetLoadingButton();
			modalReorderPosition.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err){

			modalReorderPosition.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err,translate("Erro ao reordenar dimensões"),addToast);
		}	
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete,
		actionButtonReorder
	}));	
	
	// Query Data List
	const queryListData = async () => {
		const {data} = await RCCServicesApi.getRequest({
			method: "Dimensions/ListOrder"
		});
		setItens(data);
	};

	const actionButtonDelete = (row: IDimensions) => {
		confirm(
			translate("Exclusão da dimensão"),
			`${translate("Deseja realmente excluir a dimensão")}: ${row.taa011Descricao}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Dimensions/Delete",
					urlParameters: {
						id: row.taa011Id
					}
				}); 
	
				addToast({
					color: "green",
					description: translate("Dimensão excluída com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, "Erro ao excluir dimesão", addToast);
			},
			true
		);
	};

	const canDrop = (data:any):boolean => data.nextParent?.taa011Id === data.prevParent?.taa011Id;
  
	return (
		<>	
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Inserir dimensão") : translate("Alterar dimensão"),
					icon: {
						name: "world"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="space-between"
					>
						<Title color="purple" size="md">
						    {translate("Dados Gerais")}
						</Title>
						<Audit data={selectedDimension} type="pop"/>
						<hr />
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="taa011Descricao"
							label={translate("Descrição")}
							maxLength={40}
							defaultValue={selectedDimension.taa011Descricao}
							error={errorsAddOrChange.taa011Descricao}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
						>
							<RCCSwitch 
								name="taa011Ativo" 
								label={translate("Ativo")} 
								register={registerAddOrChange} 
								defaultChecked={selectedDimension.taa011Ativo === "S" || isAdd}
							/>
						</RCCGrid>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		
			{/* Modal REORDER POSITION */}
			<Modal
				ref={modalReorderPosition}
				header={{
					title: translate("Ordenar dimensões"),
					icon: {
						name: "infoCircle"
					}
				}}
				onSubmit={submitReorder}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						alignItems="center"
						justify="space-between"
					>
						<RCCHierarchicalTree
							data={itens}
							styleInline={`
								width:100%;
								.rst__node{
									height: fit-content !important;
								
								}`
							}
							onChange={(treeData) => setItens(treeData)}
							canDrop={canDrop}

							customNode={({
								node
							}) => (
								<RCCGrid container>
									{!node.onChange && 
										<RCCGrid 
											container 
											alignItems="center"
											justify="space-between"
										>
											<span>{node.taa011Descricao}</span>												
											<RCCBadge 
												color={node.taa011Ativo === "S" ? "green" : "red"}
												styleInline="font-size: 0.83rem;"
											>
												{node.taa011Ativo  === "S" ? translate("Ativo") : translate("Inativo")}
											</RCCBadge>	
										</RCCGrid>
									}
								</RCCGrid>
							)}
						/>	
					</RCCGrid>			
				</RCCGrid>	
			</Modal>
		
		</>
	);
};

export default forwardRef(Modals);
